/**
 * UsersPanel — Settings → Users tab. Admin-only.
 *
 * Lets admins create new users (email/password), assign roles, reset
 * passwords on behalf of someone who forgot, and deactivate users.
 * Nobody is ever hard-deleted — deactivated people move to the Archived
 * sub-tab and can be restored from there. The route guard on
 * `/api/auth/users*` already enforces admin role, so the worst-case for a
 * non-admin is they see a 403 toast.
 *
 * Roles supported (must mirror backend ROLES tuple in routes/auth.py):
 *   admin, project_manager, account_manager, service_manager,
 *   lead_technician, technician, office, viewer.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useUrlTab } from "@/lib/useUrlTab";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { KeyRound, UserPlus, UserX, UsersRound, Lock, Archive, Plus } from "lucide-react";
import { btn } from "@/components/common/btn";
import { toast } from "sonner";
import { listUsers, createUser, updateUser } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { LANDING_PAGES, resolveLandingPath } from "@/lib/landingPages";
import { SettingsPanel, PanelHeader, EmptyState, LoadingState, SubTabs, TABLE_HEAD_CLASS } from "@/components/settings/SettingsUI";
import UserDrawer from "@/components/users/UserDrawer";
import PasscodeDialog from "@/components/users/PasscodeDialog";
import RolePermissionsPanel from "@/components/users/RolePermissionsPanel";
import TechniciansPanel from "@/components/users/TechniciansPanel";
import FieldUsersPanel from "@/components/users/FieldUsersPanel";
import ArchivedPanel from "@/components/users/ArchivedPanel";
import { peekFocusTarget, FOCUS_TECH } from "@/lib/deepLinkFocus";
import { Users as UsersIcon, Wrench as WrenchIcon, ShieldCheck, Smartphone } from "lucide-react";

const SUB_TABS = [
  { id: "people", label: "People", icon: UsersIcon },
  { id: "technicians", label: "Technicians", icon: WrenchIcon },
  { id: "field", label: "Field Access", icon: Smartphone },
  { id: "permissions", label: "Permissions", icon: ShieldCheck },
  { id: "archived", label: "Archived", icon: Archive, dividerBefore: true, keywords: ["deactivated", "restore", "revoked"] },
];

const defaultLanding = (u) => resolveLandingPath(u);

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "project_manager", label: "Project Manager" },
  { value: "account_manager", label: "Account Manager" },
  { value: "service_manager", label: "Service Manager" },
  { value: "lead_technician", label: "Lead Technician" },
  { value: "technician", label: "Technician" },
  { value: "accounting", label: "Accounting" },
  { value: "warehouse", label: "Warehouse (Receiving only)" },
  { value: "viewer", label: "Viewer (read-only)" },
];

// iter269 — "office" was renamed to "accounting"; map the legacy value so
// an existing office user doesn't render a blank role cell.
const ROLE_ALIASES = { office: "accounting" };
const normalizeRole = (v) => ROLE_ALIASES[v] || v || "viewer";
const roleLabel = (v) => ROLES.find(r => r.value === normalizeRole(v))?.label || v;

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Failed";
};

export default function UsersPanel() {
  const { user: currentUser, can, isNamedAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [subTab, setSubTab] = useUrlTab("sub",
    new URLSearchParams(window.location.search).get("access_request") ? "field"
      : peekFocusTarget(FOCUS_TECH) ? "technicians" : "people",
  );
  const [drawerUser, setDrawerUser] = useState(null);
  // iter293 — Mary / Nathan need the shared admin passcode before a role
  // change or delete. `pending` holds the action to run once verified.
  const [passcodePrompt, setPasscodePrompt] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listUsers();
      setUsers(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onChangeRole = async (u, role, verifyToken) => {
    if (u.passcode_protected && !verifyToken) {
      setPasscodePrompt({
        user: u,
        label: `change ${u.name || u.email}'s role to ${roleLabel(role)}`,
        run: (token) => onChangeRole(u, role, token),
      });
      return;
    }
    try {
      await updateUser(u.id, verifyToken ? { role, verify_token: verifyToken } : { role });
      toast.success(`Role updated: ${u.email} → ${roleLabel(role)}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onChangeLanding = async (u, landing_page) => {
    try {
      await updateUser(u.id, { landing_page });
      toast.success(`Landing page updated: ${u.email} → ${LANDING_PAGES.find(p => p.value === landing_page)?.label}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // Deactivate = the only way someone leaves the People list. Keeps every
  // historical link (WOs, proposals, timesheets); restore from Archived.
  const onDeactivate = async (u, verifyToken) => {
    if (u.id === currentUser?.id) {
      toast.error("You can't deactivate yourself.");
      return;
    }
    // Protected accounts get the passcode dialog as their confirmation — no double prompt.
    if (!verifyToken && !u.passcode_protected && !confirm(`Deactivate ${u.email}?\n\nThey lose access immediately, but everything they're on (work orders, proposals, timesheets) stays untouched. Restore any time from the Archived tab.`)) return;
    if (u.passcode_protected && !verifyToken) {
      setPasscodePrompt({
        user: u,
        label: `deactivate ${u.name || u.email}`,
        run: (token) => onDeactivate(u, token),
      });
      return;
    }
    try {
      await updateUser(u.id, verifyToken ? { is_disabled: true, verify_token: verifyToken } : { is_disabled: true });
      toast.success(`${u.email} deactivated — find them under Archived`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // Active = not deactivated and (for magic-link users) not revoked — both live under Archived.
  const activeUsers = users.filter((u) => !u.is_disabled && !(u.auth_provider === "magic_link" && u.field_status === "revoked"));

  return (
    <div className="space-y-3" data-testid="users-panel">
      <SubTabs items={SUB_TABS} value={subTab} onChange={setSubTab} testId="users-subtabs" testIdPrefix="users-subtab" searchable />
      {subTab === "technicians" && <TechniciansPanel />}
      {subTab === "field" && <FieldUsersPanel />}
      {subTab === "permissions" && <RolePermissionsPanel />}
      {subTab === "archived" && <ArchivedPanel onChanged={reload} />}
      {subTab === "people" && (
      <SettingsPanel>
      <PanelHeader
        icon={UsersRound}
        title="Users & Access"
        count={activeUsers.length || null}
        hint="Who can sign in and what role they have"
        actions={
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <button type="button" className={btn("write")} data-testid="btn-create-user">
                <Plus className="w-3.5 h-3.5" /> User
              </button>
            </DialogTrigger>
            <CreateUserDialog onCreated={() => { setCreateOpen(false); reload(); }} onCancel={() => setCreateOpen(false)} />
          </Dialog>
        }
      />

      {loading ? (
        <LoadingState testId="users-loading" />
      ) : activeUsers.length === 0 ? (
        <EmptyState icon={UserPlus} title="No active users" hint="Click + User to invite someone — deactivated people are under Archived." testId="users-empty" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={TABLE_HEAD_CLASS}>
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-left">Landing Page</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {activeUsers.map(u => {
                // iter299 — Service Managers run the whole Users tab; the
                // only rows they cannot touch are the two protected
                // accounts (Mary / Nathan).
                const locked = u.passcode_protected && !isNamedAdmin;
                const lockHint = `Only an admin can change ${u.name || u.email}'s account`;
                return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    <button className="hover:underline text-left" onClick={() => setDrawerUser(u)} data-testid={`btn-open-user-${u.id}`}>
                      {u.name || "—"}
                    </button>
                    {u.roster?.is_account_manager && <Badge variant="outline" className="ml-1.5 border-0 bg-blue-50 text-blue-700 text-[10px]" data-testid={`badge-am-${u.id}`}>AM</Badge>}
                    {u.roster?.is_project_manager && <Badge variant="outline" className="ml-1 border-0 bg-violet-50 text-violet-700 text-[10px]" data-testid={`badge-pm-${u.id}`}>PM</Badge>}
                    {u.is_tech && <Badge variant="outline" className="ml-1 border-0 bg-emerald-50 text-emerald-700 text-[10px]" data-testid={`badge-tech-${u.id}`}>{u.is_lead_tech ? "LEAD TECH" : "TECH"}</Badge>}
                    {u.auth_provider === "magic_link" && <Badge variant="outline" className="ml-1 border-0 bg-sky-50 text-sky-700 text-[10px]" data-testid={`badge-field-${u.id}`}>FIELD · {u.field_status}</Badge>}
                    {u.login_enabled === false && <Badge variant="outline" className="ml-1 border-0 bg-slate-100 text-slate-500 text-[10px]" data-testid={`badge-no-login-${u.id}`}>NO LOGIN</Badge>}
                  </td>
                  <td className="px-3 py-2 text-slate-600 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2">
                    {locked && (
                      <span className="block text-[10px] text-slate-400 mb-0.5" data-testid={`user-locked-${u.id}`}>
                        <Lock className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />Admin only
                      </span>
                    )}
                    <Select value={normalizeRole(u.role)} onValueChange={(v) => onChangeRole(u, v)} disabled={locked}>
                      <SelectTrigger className="h-7 text-xs w-44" data-testid={`select-role-${u.id}`} title={locked ? lockHint : undefined}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(u.auth_provider === "magic_link"
                          ? ROLES.filter(r => r.value === "technician")
                          : ROLES
                        ).map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Select value={defaultLanding(u)} onValueChange={(v) => onChangeLanding(u, v)}>
                      <SelectTrigger className="h-7 text-xs w-40" data-testid={`select-landing-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LANDING_PAGES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setResetTarget(u)} disabled={locked} title={locked ? lockHint : undefined} data-testid={`btn-reset-pw-${u.id}`}>
                      <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 px-2 text-xs text-amber-600 hover:bg-amber-50"
                      onClick={() => onDeactivate(u)}
                      disabled={locked}
                      title={locked ? lockHint : "Deactivate — blocks access immediately, keeps all historical data. Restore from Archived."}
                      data-testid={`btn-deactivate-user-${u.id}`}
                    >
                      <UserX className="w-3.5 h-3.5 mr-1" /> Deactivate
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resetTarget && (
        <ResetPasswordDialog
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => { setResetTarget(null); toast.success("Password reset"); }}
        />
      )}
      </SettingsPanel>
      )}

      {drawerUser && (
        <UserDrawer
          user={drawerUser}
          canWrite={can("settings.users", "write")}
          onClose={() => setDrawerUser(null)}
          onSaved={() => { setDrawerUser(null); reload(); }}
        />
      )}

      {passcodePrompt && (
        <PasscodeDialog
          label={passcodePrompt.label}
          onClose={() => setPasscodePrompt(null)}
          onVerified={(token) => { const { run } = passcodePrompt; setPasscodePrompt(null); run(token); }}
        />
      )}
    </div>
  );
}

function CreateUserDialog({ onCreated, onCancel }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("viewer");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createUser({ name: name.trim(), email: email.trim().toLowerCase(), password, role });
      toast.success("User created");
      onCreated();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-sm" data-testid="dialog-create-user">
      <DialogHeader>
        <DialogTitle>Add User</DialogTitle>
        <DialogDescription>Create a new sign-in account. Roles can be changed any time from the Users list.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="cu-name" className="text-xs">Name</Label>
          <Input id="cu-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="input-create-name" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cu-email" className="text-xs">Email</Label>
          <Input id="cu-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="input-create-email" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cu-pw" className="text-xs">Password (min 8 chars)</Label>
          <Input id="cu-pw" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-create-password" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-9" data-testid="select-create-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={submitting} data-testid="btn-submit-create-user">
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}


function ResetPasswordDialog({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await updateUser(user.id, { password: pw });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="dialog-reset-password">
        <DialogHeader>
          <DialogTitle>Reset password for {user.email}</DialogTitle>
          <DialogDescription>The user will use this new password on their next sign-in. Their previous password is invalidated immediately.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">New password (min 8 chars)</Label>
            <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="input-reset-password" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="btn-submit-reset-password">
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
